// Phase 4 OM — Per-state action surfaces for supply WorkOrderDetail.
//
// Volkan ratify 2026-06-03 + Bölüm 10 Adım 7-9 + memory
// feedback_fulfillment_mode_c_staging_per_product. Three panels gated by
// current WO state:
//   in_production           → WorkOrderQCPanel (QC outcome + rework counter)
//                            + WorkOrderDispatchModal trigger (carrier/tracking)
//   dispatched_to_warehouse → WorkOrderScanPanel (Aybars NJ QR scan)
//
// S8 invariant: supply surface NEVER renders seller/customer info. Only
// WO ID + product + qty + producer-side metadata. Aybars/Mehmet/Hakan
// share these panels but persona-filtered handler scopes WO visibility.

import { useId, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, AlertCircle, Send, QrCode } from 'lucide-react'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { Button } from '../../components/ui/button'
import { apiUrl } from '../../lib/api'
import { toast } from '../../lib/toast'
import { isValidScanToken } from '../../lib/scan-token-validator'
import { useT } from '../../i18n'
import { workOrderOperationsI18n } from '../../i18n/supply/work-order-operations'
import { SUPPLY_WORK_ORDERS_STRINGS } from '../../i18n/supply/work-orders'
import type { WorkOrder } from '../../mocks/data/work-orders'
import { apiErrorCode, apiErrorMessage, hasPassedLatestQc } from './workOrderDisplay'

type Props = { wo: WorkOrder; persona: string }

export function WorkOrderOperationsPanels({ wo, persona }: Props) {
  if (wo.state === 'in_production') {
    return (
      <>
        <WorkOrderQCPanel wo={wo} persona={persona} />
        <WorkOrderDispatchTrigger wo={wo} persona={persona} />
      </>
    )
  }
  if (wo.state === 'dispatched_to_warehouse') {
    return <WorkOrderScanPanel wo={wo} persona={persona} />
  }
  return null
}

// ============== WorkOrderQCPanel ==============

function WorkOrderQCPanel({ wo, persona }: { wo: WorkOrder; persona: string }) {
  const t = useT(workOrderOperationsI18n).qc
  const qc = useQueryClient()
  const [unitsInspected, setUnitsInspected] = useState(wo.quantity)
  const [unitsPassed, setUnitsPassed] = useState(wo.quantity)
  const [unitsFailed, setUnitsFailed] = useState(0)
  const [reworkReason, setReworkReason] = useState('')
  const [outcome, setOutcome] = useState<'passed' | 'rework'>('passed')

  const reworkAttempts = wo.rework_attempts ?? 0
  const maxedOut = reworkAttempts >= 3

  const qcMut = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        apiUrl(`/supply/my-work-orders/${wo.id}/qc-event?persona=${persona}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            outcome,
            units_inspected: unitsInspected,
            units_passed: outcome === 'passed' ? unitsPassed : 0,
            units_failed: outcome === 'rework' ? unitsFailed : 0,
            rework_reason: outcome === 'rework' ? reworkReason : undefined,
          }),
        },
      )
      if (!res.ok) {
        const p = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
        throw new Error(p.detail ?? `HTTP ${res.status}`)
      }
      return res.json() as Promise<WorkOrder>
    },
    onSuccess: () => {
      toast.success(
        outcome === 'passed' ? t.passSuccessTitle : t.reworkSuccessTitle,
        {
          description:
            outcome === 'passed'
              ? t.passSuccessDesc(unitsPassed, unitsInspected)
              : t.reworkSuccessDesc(reworkAttempts + 1),
          audience: 'supply',
        },
      )
      qc.invalidateQueries({ queryKey: ['supply', 'work-orders', persona, wo.id] })
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] })
    },
    onError: (err) => {
      toast.error(t.errorTitle, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'supply',
      })
    },
  })

  const qcEvents =
    (wo.events ?? []).filter(
      (e) => e.kind === 'qc_completed' || e.kind === 'rework_completed',
    ) ?? []

  return (
    <section className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text)]">
          {t.title}
        </h3>
        <span className="text-xs text-[var(--color-text-muted)]">
          {t.reworkAttempts(reworkAttempts)}
        </span>
      </header>

      {maxedOut && (
        <div className="mt-3 rounded-md border-l-4 border-l-rose-500 bg-rose-50 px-3 py-2 text-sm text-rose-900">
          <AlertCircle className="mr-1 inline h-4 w-4" />
          {t.disputeRequired}
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-xs">
          <span className="font-medium text-[var(--color-text)]">{t.unitsInspectedLabel}</span>
          <input
            type="number"
            min={0}
            max={wo.quantity}
            value={unitsInspected}
            onChange={(e) => setUnitsInspected(Number(e.target.value))}
            disabled={qcMut.isPending || maxedOut}
            className="mt-1 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1 text-sm text-[var(--color-text)] tabular-nums focus-visible:shadow-[var(--shadow-focus)]"
          />
        </label>
        {outcome === 'passed' ? (
          <label className="text-xs">
            <span className="font-medium text-[var(--color-success)]">{t.unitsPassedLabel}</span>
            <input
              type="number"
              min={0}
              max={unitsInspected}
              value={unitsPassed}
              onChange={(e) => setUnitsPassed(Number(e.target.value))}
              disabled={qcMut.isPending || maxedOut}
              className="mt-1 w-full rounded-md border border-[var(--color-success)] bg-[var(--color-surface)] px-2 py-1 text-sm text-[var(--color-text)] tabular-nums focus-visible:shadow-[var(--shadow-focus)]"
            />
          </label>
        ) : (
          <label className="text-xs">
            <span className="font-medium text-[var(--color-danger)]">{t.unitsFailedLabel}</span>
            <input
              type="number"
              min={0}
              max={unitsInspected}
              value={unitsFailed}
              onChange={(e) => setUnitsFailed(Number(e.target.value))}
              disabled={qcMut.isPending || maxedOut}
              className="mt-1 w-full rounded-md border border-[var(--color-danger)] bg-[var(--color-surface)] px-2 py-1 text-sm text-[var(--color-text)] tabular-nums focus-visible:shadow-[var(--shadow-focus)]"
            />
          </label>
        )}
        <div className="text-xs">
          <span className="font-medium text-[var(--color-text)]">{t.outcomeLabel}</span>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={() => setOutcome('passed')}
              disabled={qcMut.isPending || maxedOut}
              className={
                'rounded-md px-3 py-1 text-xs font-medium ring-1 ' +
                (outcome === 'passed'
                  ? 'bg-emerald-700 text-white ring-emerald-700'
                  : 'bg-[var(--color-surface)] text-[var(--color-text)] ring-[var(--color-border-strong)]')
              }
            >
              {t.outcomePassed}
            </button>
            <button
              type="button"
              onClick={() => setOutcome('rework')}
              disabled={qcMut.isPending || maxedOut}
              className={
                'rounded-md px-3 py-1 text-xs font-medium ring-1 ' +
                (outcome === 'rework'
                  ? 'bg-rose-700 text-white ring-rose-700'
                  : 'bg-[var(--color-surface)] text-[var(--color-text)] ring-[var(--color-border-strong)]')
              }
            >
              {t.outcomeRework}
            </button>
          </div>
        </div>
      </div>

      {outcome === 'rework' && (
        <div className="mt-3">
          <label className="text-xs font-medium text-[var(--color-text)]">
            {t.reworkReasonLabel}
          </label>
          <input
            type="text"
            value={reworkReason}
            onChange={(e) => setReworkReason(e.target.value)}
            placeholder={t.reworkReasonPlaceholder}
            disabled={qcMut.isPending || maxedOut}
            className="mt-1 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus-visible:shadow-[var(--shadow-focus)]"
          />
        </div>
      )}

      <div className="mt-4">
        <Button
          type="button"
          onClick={() => qcMut.mutate()}
          disabled={qcMut.isPending || maxedOut}
        >
          {qcMut.isPending ? t.submitting : t.submitButton}
        </Button>
      </div>

      {qcEvents.length > 0 && (
        <div className="mt-4 border-t border-[var(--color-border)] pt-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            {t.eventLogLabel}
          </div>
          <ul className="mt-2 space-y-1">
            {qcEvents.map((ev) => (
              <li key={ev.id} className="text-xs text-[var(--color-text)]">
                <span
                  className={
                    'inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ' +
                    (ev.kind === 'qc_completed'
                      ? 'bg-emerald-100 text-emerald-800 ring-emerald-200'
                      : 'bg-rose-100 text-rose-800 ring-rose-200')
                  }
                >
                  {ev.kind === 'qc_completed' ? t.eventPassedTag : t.eventReworkTag}
                </span>{' '}
                <span className="text-[var(--color-text-muted)]">{new Date(ev.at).toLocaleString()}</span>
                {ev.meta?.rework_reason && (
                  <span className="ml-2 text-[var(--color-text-muted)]">· {ev.meta.rework_reason}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// ============== WorkOrderDispatchTrigger + Modal ==============

function WorkOrderDispatchTrigger({ wo, persona }: { wo: WorkOrder; persona: string }) {
  const t = useT(workOrderOperationsI18n).dispatch
  const [open, setOpen] = useState(false)
  const noteId = useId()
  // The one dispatch path (UX-20261101-01): opens only when the latest QC
  // result is a pass. The server refuses /dispatch on the same rule (409).
  const hasPassedQc = hasPassedLatestQc(wo.events)

  return (
    <section className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text)]">
          {t.title}
        </h3>
        <span className="text-xs text-[var(--color-text-muted)]">
          {wo.fulfillment_mode === 'hub_via_nj' ? t.modeHub : t.modeDropship}
        </span>
      </header>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        {wo.fulfillment_mode === 'hub_via_nj' ? t.subtitleHub : t.subtitleDropship}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!hasPassedQc}
          aria-describedby={hasPassedQc ? undefined : noteId}
        >
          <Send className="mr-1 h-4 w-4" />
          {t.dispatchButton}
        </Button>
        {!hasPassedQc && (
          <span id={noteId} className="text-xs text-[var(--color-text-muted)]">
            {t.requiresQcNote}
          </span>
        )}
      </div>
      <WorkOrderDispatchModal
        wo={wo}
        persona={persona}
        open={open}
        onClose={() => setOpen(false)}
      />
    </section>
  )
}

function WorkOrderDispatchModal({
  wo,
  persona,
  open,
  onClose,
}: {
  wo: WorkOrder
  persona: string
  open: boolean
  onClose: () => void
}) {
  const t = useT(workOrderOperationsI18n).dispatch
  const statusLabel = useT(SUPPLY_WORK_ORDERS_STRINGS).workOrderDetail.statusLabel
  const qc = useQueryClient()
  // No preselected carrier: the producer must pick the real one (UX-20261031-11).
  const [carrier, setCarrier] = useState('')
  const [tracking, setTracking] = useState('')
  const [note, setNote] = useState('')
  const fieldId = useId()
  const carrierId = `${fieldId}-carrier`
  const trackingId = `${fieldId}-tracking`
  const noteId = `${fieldId}-note`
  const hintId = `${fieldId}-hint`

  const dispatchMut = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        apiUrl(`/supply/my-work-orders/${wo.id}/dispatch?persona=${persona}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ carrier, tracking: tracking.trim(), note }),
        },
      )
      if (!res.ok) {
        const p: unknown = await res.json().catch(() => null)
        if (apiErrorCode(p) === 'dispatch_qc_not_passed') {
          throw new Error(t.qcNotPassedError)
        }
        throw new Error(apiErrorMessage(p, res.status))
      }
      return res.json() as Promise<WorkOrder>
    },
    onSuccess: () => {
      toast.success(t.dispatchSuccessTitle, {
        description: t.dispatchSuccessDesc(carrier, tracking),
        audience: 'supply',
      })
      onClose()
      setCarrier('')
      setTracking('')
      setNote('')
      qc.invalidateQueries({ queryKey: ['supply', 'work-orders', persona, wo.id] })
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] })
    },
    onError: (err) => {
      toast.error(t.dispatchErrorTitle, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'supply',
      })
    },
  })

  const missingRequired = !carrier || !tracking.trim()
  const disabled = dispatchMut.isPending || missingRequired
  const targetStatus =
    wo.fulfillment_mode === 'hub_via_nj'
      ? statusLabel.dispatchedToWarehouse
      : statusLabel.dispatchedToCustomer

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.dialogTitle}</DialogTitle>
          <DialogDescription>
            {wo.fulfillment_mode === 'hub_via_nj'
              ? t.dialogDescHub
              : t.dialogDescDropship}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-text)]"
            data-testid="dispatch-summary"
          >
            <p className="text-xs font-semibold text-[var(--color-text-muted)]">{t.summaryTitle}</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>{t.summaryItem(wo.product_name, wo.quantity)}</li>
              <li>{t.summaryStatus(statusLabel.inProduction, targetStatus)}</li>
              <li>{t.summaryFinal}</li>
            </ul>
          </div>
          <div>
            <label
              htmlFor={carrierId}
              className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]"
            >
              {t.carrierLabel}
            </label>
            <select
              id={carrierId}
              required
              aria-required="true"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              disabled={dispatchMut.isPending}
              className="mt-1 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1.5 text-sm text-[var(--color-text)]"
            >
              <option value="" disabled>
                {t.carrierPlaceholder}
              </option>
              <option value="DHL">DHL</option>
              <option value="UPS">UPS</option>
              <option value="USPS">USPS</option>
              <option value="FedEx">FedEx</option>
              <option value="Aras">Aras Kargo</option>
            </select>
          </div>
          <div>
            <label
              htmlFor={trackingId}
              className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]"
            >
              {t.trackingLabel}
            </label>
            <input
              id={trackingId}
              type="text"
              required
              aria-required="true"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              disabled={dispatchMut.isPending}
              placeholder={t.trackingPlaceholder}
              className="mt-1 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
            />
          </div>
          <div>
            <label
              htmlFor={noteId}
              className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]"
            >
              {t.noteLabel}
            </label>
            <input
              id={noteId}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={dispatchMut.isPending}
              placeholder={t.notePlaceholder}
              className="mt-1 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
            />
          </div>
          {missingRequired && (
            <p id={hintId} className="text-xs text-[var(--color-text-muted)]">
              {t.requiredHint}
            </p>
          )}
          {dispatchMut.error instanceof Error && (
            <p role="alert" className="text-sm text-[var(--color-danger)]">
              {dispatchMut.error.message}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={dispatchMut.isPending}>
            {t.cancel}
          </Button>
          <Button
            type="button"
            onClick={() => dispatchMut.mutate()}
            disabled={disabled}
            aria-describedby={missingRequired ? hintId : undefined}
          >
            {dispatchMut.isPending ? t.dispatching : t.confirmButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============== WorkOrderScanPanel (Aybars NJ QR scan) ==============

function WorkOrderScanPanel({ wo, persona }: { wo: WorkOrder; persona: string }) {
  const t = useT(workOrderOperationsI18n).scan
  const qc = useQueryClient()
  const [scanToken, setScanToken] = useState('')
  const [note, setNote] = useState('')

  const receiveMut = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        apiUrl(`/supply/my-work-orders/${wo.id}/receive?persona=${persona}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scan_token: scanToken, note }),
        },
      )
      if (!res.ok) {
        const p = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
        throw new Error(p.detail ?? `HTTP ${res.status}`)
      }
      return res.json() as Promise<WorkOrder>
    },
    onSuccess: () => {
      toast.success(t.successTitle, {
        description: t.successDesc(wo.product_name),
        audience: 'supply',
      })
      setScanToken('')
      setNote('')
      qc.invalidateQueries({ queryKey: ['supply', 'work-orders', persona, wo.id] })
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] })
    },
    onError: (err) => {
      toast.error(t.errorTitle, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'supply',
      })
    },
  })

  const dispatchEvent = (wo.events ?? []).find((e) => e.kind === 'dispatched')
  const tokenValid = isValidScanToken(scanToken)

  return (
    <section className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4">
      <header className="flex items-center gap-2">
        <QrCode className="h-5 w-5 text-[var(--color-success)]" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text)]">
          {t.title}
        </h3>
      </header>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t.subtitle}</p>

      {dispatchEvent?.meta?.tracking && (
        <div className="mt-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-text)]">
          <div>
            <span className="font-medium">{t.dispatchedAt}:</span>{' '}
            <span className="text-[var(--color-text-muted)]">
              {new Date(dispatchEvent.at).toLocaleString()}
            </span>
          </div>
          <div className="mt-0.5">
            <span className="font-medium">{t.carrierTracking}:</span>{' '}
            <span className="font-mono">
              {dispatchEvent.meta.carrier} {dispatchEvent.meta.tracking}
            </span>
          </div>
        </div>
      )}

      <div className="mt-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.scanTokenLabel}
        </label>
        <input
          type="text"
          value={scanToken}
          onChange={(e) => setScanToken(e.target.value)}
          disabled={receiveMut.isPending}
          placeholder={t.scanTokenPlaceholder}
          className={
            'mt-1 w-full rounded-md border bg-[var(--color-surface)] px-2 py-1.5 font-mono text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] ' +
            (scanToken.length === 0
              ? 'border-[var(--color-border-strong)]'
              : tokenValid
                ? 'border-[var(--color-success)]'
                : 'border-[var(--color-danger)]')
          }
        />
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">{t.scanTokenNote}</p>
      </div>

      <div className="mt-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.noteLabel}
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={receiveMut.isPending}
          placeholder={t.notePlaceholder}
          className="mt-1 w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
        />
      </div>

      <div className="mt-4">
        <Button
          type="button"
          onClick={() => receiveMut.mutate()}
          disabled={receiveMut.isPending || !tokenValid}
        >
          <CheckCircle2 className="mr-1 h-4 w-4" />
          {receiveMut.isPending ? t.submitting : t.submitButton}
        </Button>
      </div>
    </section>
  )
}
